import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useHelp } from '../../context/HelpContext';
import { useIsResizing } from './useIsResizing';
import { trackHelpPanelOpened, trackHelpArticleClicked, trackHelpSupportCenterClicked, trackHelpContactClicked } from '../../analytics/analytics';

const SUPPORT_CENTER_URL = 'https://support.earthranger.com/en_US/gundi';

const HelpSidePanel: React.FC = () => {
    const { t } = useTranslation();
    const { helpContent, isPanelOpen, openPanel, closePanel, rightOffsetPx } = useHelp();
    const [buttonVisible, setButtonVisible] = useState(false);
    // While resizing, drop the slide transition so the width switch at the md breakpoint
    // doesn't animate the closed (translate-x-full) panel across the viewport.
    const isResizing = useIsResizing();
    const location = useLocation();

    useEffect(() => {
        const timer = setTimeout(() => setButtonVisible(true), 350);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        closePanel();
    }, [location.pathname, closePanel]);

    return (
        <>
            {/* Floating ? button */}
            {!isPanelOpen && (
                <button
                    onClick={() => { trackHelpPanelOpened(); openPanel(); }}
                    className={`fixed bottom-6 z-[57] md:z-[55] w-12 h-12 rounded-full backdrop-blur-[1.5px] flex items-center justify-center hover:opacity-90 transition-all duration-[600ms] ${buttonVisible ? 'opacity-100 scale-100 translate-y-0 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]' : 'opacity-0 scale-0 translate-y-8'}`}
                    style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.80)',
                        filter: 'drop-shadow(0 0 10px rgba(0, 0, 0, 0.25))',
                        right: 24 + rightOffsetPx,
                        transition: 'right 300ms, opacity 600ms, transform 600ms',
                    }}
                    aria-label={t('Open Help Panel')}
                    data-testid="HelpSidePanel-floating-button"
                >
                    <span className="text-status-green text-xl font-bold leading-none">?</span>
                </button>
            )}

            {/* Side panel — full-screen below md (stacks on top of a full-screen Rules panel,
                z-58 > Rules z-56); fixed-width and side-by-side at md+. */}
            <div
                className={`fixed top-0 h-full w-full md:w-[383px] bg-white shadow-xl z-[58] md:z-[60] transition-transform duration-300 flex flex-col ${isPanelOpen ? 'translate-x-0' : 'translate-x-full'}`}
                style={{ right: rightOffsetPx, transition: isResizing ? 'none' : 'transform 300ms, right 300ms' }}
                data-testid="HelpSidePanel-panel"
                aria-hidden={!isPanelOpen}
                {...(!isPanelOpen && { inert: '' } as unknown as React.HTMLAttributes<HTMLDivElement>)}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-4">
                    <a
                        href={SUPPORT_CENTER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={trackHelpSupportCenterClicked}
                        className="inline-flex h-[36px] px-3 py-2 justify-center items-center gap-2 rounded-[3px] border border-dark-green text-dark-green text-[16px] font-[590] leading-normal no-underline hover:bg-light-green transition-colors duration-300"
                        data-testid="HelpSidePanel-support-center-button"
                    >
                        {t('Support Center')}
                    </a>
                    <button
                        onClick={closePanel}
                        className="w-[36px] h-[36px] aspect-square flex items-center justify-center text-medium-gray hover:text-off-black transition-colors duration-200 text-[21px] leading-none"
                        aria-label={t('Close')}
                        data-testid="HelpSidePanel-close-button"
                    >
                        ✕
                    </button>
                </div>
                <hr className="mt-[12px] border-t border-gray-lines" />

                {/* Articles */}
                <div className="flex-1 overflow-y-auto px-4 pb-6">
                    {helpContent ? <>
                    <h2 className="text-[24px] font-normal leading-normal text-grey-g0 mb-3 mt-[28px]">
                        {t('Help Center Articles')}
                    </h2>
                    <div className="flex flex-col gap-4">
                        {helpContent.cards.map((card) => (
                            <div key={card.button.url} className="flex flex-col items-start rounded p-4 gap-[10px]" style={{ backgroundColor: '#EBF4EB' }}>
                                <h3 className="text-[18px] font-[510] leading-normal self-stretch text-dark-green">
                                    {card.title}
                                </h3>
                                <p className="text-[16px] font-normal leading-normal self-stretch text-grey-g0">
                                    {card.description}
                                </p>
                                <a
                                    href={card.button.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackHelpArticleClicked(card.title, card.button.url)}
                                    className="flex items-center justify-center gap-2 h-[36px] px-3 py-2 rounded-[3px] text-[16px] font-[590] leading-normal text-white no-underline bg-dark-green hover:bg-status-green transition-colors duration-300 mt-1"
                                >
                                    {card.button.label}
                                </a>
                            </div>
                        ))}
                    </div>
                    </> : <>
                        <h2 className="text-[24px] font-normal leading-normal text-grey-g0 mb-3 mt-[28px]">
                            {t('Get Support')}
                        </h2>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col items-start rounded p-4 gap-[10px]" style={{ backgroundColor: '#EBF4EB' }}>
                                <h3 className="text-[18px] font-[510] leading-normal self-stretch text-dark-green">
                                    {t('Support Center')}
                                </h3>
                                <p className="text-[16px] font-normal leading-normal self-stretch text-grey-g0">
                                    {t('Browse the Support Center for guides and resources.')}
                                </p>
                                <a
                                    href="https://support.earthranger.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={trackHelpSupportCenterClicked}
                                    className="flex items-center justify-center gap-2 h-[36px] px-3 py-2 rounded-[3px] text-[16px] font-[590] leading-normal text-white no-underline bg-dark-green hover:bg-status-green transition-colors duration-300 mt-1"
                                >
                                    {t('Visit')}
                                </a>
                            </div>
                            <div className="flex flex-col items-start rounded p-4 gap-[10px]" style={{ backgroundColor: '#EBF4EB' }}>
                                <h3 className="text-[18px] font-[510] leading-normal self-stretch text-dark-green">
                                    {t('Contact Support Team')}
                                </h3>
                                <p className="text-[16px] font-normal leading-normal self-stretch text-grey-g0">
                                    {t('Have a question or need help with your setup? Reach out to our team and we\'ll get back to you as soon as possible.')}
                                </p>
                                <a
                                    href="https://support.earthranger.com/en_US/gundi-getting-help/gundi-contact-us"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={trackHelpContactClicked}
                                    className="flex items-center justify-center gap-2 h-[36px] px-3 py-2 rounded-[3px] text-[16px] font-[590] leading-normal text-white no-underline bg-dark-green hover:bg-status-green transition-colors duration-300 mt-1"
                                >
                                    {t('Contact')}
                                </a>
                            </div>
                        </div>
                    </>}
                </div>
                {/* Footer links */}
                <div className="px-4 py-4 border-t border-gray-lines flex flex-col gap-2">
                    <a href="https://projectgundi.org/Legal-Pages/User-Agreement" target="_blank" rel="noopener noreferrer"
                        className="text-sm text-medium-gray hover:text-off-black transition-colors duration-200">
                        {t('Gundi EULA')}
                    </a>
                </div>
            </div>
        </>
    );
};

export default HelpSidePanel;
